import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, XCircle, BookmarkSimple, ArrowRight, Flame, Sun, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import SetDialog from "@/components/SetDialog";

export default function Practice({ mode = "practice" }) {
    const location = useLocation();
    const navigate = useNavigate();
    const preloaded = location.state?.questions;
    const preloadedLabel = location.state?.label;

    const [domains, setDomains] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState(() => localStorage.getItem("crma_pref_domain") || "__all__");
    const [selectedDifficulty, setSelectedDifficulty] = useState(() => localStorage.getItem("crma_pref_difficulty") || "__any__");
    const [questions, setQuestions] = useState([]);
    const [idx, setIdx] = useState(0);
    const [selected, setSelected] = useState(null);
    const [reveal, setReveal] = useState(false);
    const [answers, setAnswers] = useState({});
    const [started, setStarted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(1800);
    const [startTime, setStartTime] = useState(null);
    const [result, setResult] = useState(null);
    const [bookmarks, setBookmarks] = useState(new Set());
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [saveIds, setSaveIds] = useState([]);
    const [saveDefaultEmoji, setSaveDefaultEmoji] = useState("📚");
    const [questionCount, setQuestionCount] = useState(() => {
        const v = parseInt(localStorage.getItem("crma_pref_count") || "20", 10);
        return isNaN(v) ? 20 : v;
    });

    // Load prefs from backend on mount; save to backend on change
    useEffect(() => {
        api.get("/user/prefs").then(({ data }) => {
            if (data.domain) setSelectedDomain(data.domain);
            if (data.difficulty) setSelectedDifficulty(data.difficulty);
            if (data.question_count) setQuestionCount(data.question_count);
        }).catch(() => {});
    }, []);

    useEffect(() => { localStorage.setItem("crma_pref_domain", selectedDomain); }, [selectedDomain]);
    useEffect(() => { localStorage.setItem("crma_pref_difficulty", selectedDifficulty); }, [selectedDifficulty]);
    useEffect(() => { localStorage.setItem("crma_pref_count", String(questionCount)); }, [questionCount]);

    // Debounced backend sync
    useEffect(() => {
        const t = setTimeout(() => {
            api.put("/user/prefs", {
                domain: selectedDomain,
                difficulty: selectedDifficulty,
                question_count: questionCount,
            }).catch(() => {});
        }, 800);
        return () => clearTimeout(t);
    }, [selectedDomain, selectedDifficulty, questionCount]);

    const isExam = mode === "exam";

    useEffect(() => {
        api.get("/domains").then((r) => setDomains(r.data.domains || [])).catch(() => {});
        api.get("/bookmarks").then((r) => setBookmarks(new Set((r.data.items || []).map((q) => q.id)))).catch(() => {});
    }, []);

    // Auto-start when preloaded questions are provided (e.g., retake wrong)
    useEffect(() => {
        if (preloaded && preloaded.length > 0 && !started && !result) {
            setQuestions(preloaded);
            setIdx(0);
            setSelected(null);
            setReveal(false);
            setAnswers({});
            setStarted(true);
            setStartTime(Date.now());
            if (isExam) setTimeLeft(1800);
            // Clear router state so refresh doesn't re-trigger
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [preloaded]);

    useEffect(() => {
        if (!started || !isExam) return;
        if (timeLeft <= 0) {
            submit();
            return;
        }
        const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [started, timeLeft, isExam]);

    const startSession = async () => {
        const params = {};
        if (selectedDomain !== "__all__") params.domain = selectedDomain;
        if (selectedDifficulty === "balanced") {
            params.mix = "balanced";
        } else if (selectedDifficulty !== "__any__") {
            params.difficulty = selectedDifficulty;
        }
        params.limit = questionCount;
        try {
            const { data } = await api.get("/questions", { params });
            if (!data.items || data.items.length === 0) {
                toast.error("No questions match your filters. Try broader settings.");
                return;
            }
            setQuestions(data.items);
            setIdx(0);
            setSelected(null);
            setReveal(false);
            setAnswers({});
            setStarted(true);
            setResult(null);
            setStartTime(Date.now());
            if (isExam) setTimeLeft(questionCount * 90); // 90 seconds per question
        } catch (e) {
            toast.error("Failed to load questions");
        }
    };

    const q = questions[idx];

    const pick = (i) => {
        if (isExam) {
            setSelected(i);
            setAnswers((a) => ({ ...a, [q.id]: i }));
        } else {
            if (reveal) return;
            setSelected(i);
            setReveal(true);
            setAnswers((a) => ({ ...a, [q.id]: i }));
        }
    };

    const next = () => {
        setSelected(answers[questions[idx + 1]?.id] ?? null);
        setReveal(!isExam ? false : false);
        setIdx((i) => i + 1);
    };

    const prev = () => {
        setSelected(answers[questions[idx - 1]?.id] ?? null);
        setReveal(false);
        setIdx((i) => i - 1);
    };

    const submit = async () => {
        const payloadAnswers = Object.entries(answers).map(([question_id, selected_index]) => ({ question_id, selected_index }));
        if (payloadAnswers.length === 0) {
            toast.error("Answer at least one question");
            return;
        }
        try {
            const { data } = await api.post("/attempts", {
                mode,
                domain: selectedDomain === "__all__" ? null : selectedDomain,
                answers: payloadAnswers,
                duration_seconds: Math.floor((Date.now() - startTime) / 1000),
            });
            setResult(data);
            setStarted(false);
        } catch (e) {
            toast.error("Submission failed");
        }
    };

    const toggleBookmark = async () => {
        if (!q) return;
        try {
            const { data } = await api.post("/bookmarks/toggle", { question_id: q.id });
            setBookmarks((s) => {
                const n = new Set(s);
                if (data.bookmarked) n.add(q.id); else n.delete(q.id);
                return n;
            });
        } catch (err) { console.error("Bookmark toggle failed:", err); }
    };

    const timeStr = useMemo(() => {
        const m = Math.floor(timeLeft / 60).toString().padStart(2, "0");
        const s = (timeLeft % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    }, [timeLeft]);

    if (result) {
        return (
            <div className="mx-auto max-w-3xl px-6 py-16" data-testid="result-screen">
                <div className="overline mb-3 text-primary">Session complete</div>
                <h1 className="font-display text-5xl font-semibold tracking-tight">You scored {result.score_pct}%</h1>
                <p className="mt-4 text-muted-foreground">{result.correct} correct out of {result.total} · {mode} mode</p>
                <div className="mt-10 space-y-4">
                    {result.details.map((d, i) => (
                        <div key={d.question_id || i} className={`border p-6 ${d.is_correct ? "border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-destructive/40 bg-destructive/5"}`} data-testid={`result-detail-${i}`}>
                            <div className="flex items-start gap-3">
                                {d.is_correct ? <CheckCircle weight="fill" className="mt-1 flex-shrink-0 text-emerald-600" size={20} /> : <XCircle weight="fill" className="mt-1 flex-shrink-0 text-destructive" size={20} />}
                                <div>
                                    <div className="font-medium">Question {i + 1}</div>
                                    <div className="mt-2 text-sm">Your answer: {String.fromCharCode(65 + d.selected_index)} · Correct: {String.fromCharCode(65 + d.correct_index)}</div>
                                    {d.explanation && <div className="mt-3 text-sm text-muted-foreground">{d.explanation}</div>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-10 flex flex-wrap gap-3">
                    <Button onClick={() => setResult(null)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="result-restart">Try another</Button>
                    <Button
                        variant="outline"
                        onClick={() => {
                            const wrongIds = result.details.filter((d) => !d.is_correct).map((d) => d.question_id);
                            if (wrongIds.length === 0) { toast.success("Perfect score — nothing to save!"); return; }
                            setSaveIds(wrongIds);
                            setSaveDefaultEmoji("🎯");
                            setSaveDialogOpen(true);
                        }}
                        data-testid="result-save-wrong"
                    >
                        Save wrong as set
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={() => {
                            const ids = result.details.map((d) => d.question_id);
                            setSaveIds(ids);
                            setSaveDefaultEmoji("📚");
                            setSaveDialogOpen(true);
                        }}
                        data-testid="result-save-all"
                    >
                        Save entire session as set
                    </Button>
                </div>
                <SetDialog
                    open={saveDialogOpen}
                    onOpenChange={setSaveDialogOpen}
                    initial={{ emoji: saveDefaultEmoji, name: "" }}
                    title="Save as study set"
                    onSubmit={async (payload) => {
                        try {
                            await api.post("/sets", { ...payload, question_ids: saveIds });
                            toast.success(`Saved "${payload.name}"`);
                        } catch { toast.error("Failed to save set"); }
                    }}
                />
            </div>
        );
    }

    if (!started) {
        return (
            <div className="mx-auto max-w-2xl px-6 py-16" data-testid="start-screen">
                <div className="overline mb-3 text-primary">{isExam ? "Exam mode" : "Practice mode"}</div>
                <h1 className="font-display text-5xl font-semibold tracking-tight">{isExam ? "Sit a timed simulation." : "Learn with instant feedback."}</h1>
                <p className="mt-4 max-w-md text-muted-foreground">
                    {isExam ? `${questionCount} questions · ${Math.round((questionCount * 90) / 60)} minutes · results at the end.` : "Answer at your own pace. Explanations revealed after each choice."}
                </p>
                <div className="mt-10 space-y-4 border border-border bg-card p-6">
                    <div>
                        <label className="overline text-muted-foreground">Quick presets</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => { setSelectedDifficulty("easy"); setTimeout(startSession, 50); }}
                                data-testid="preset-warmup"
                            >
                                <Sun size={14} className="mr-1" /> Warmup (all easy)
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => { setSelectedDifficulty("balanced"); setTimeout(startSession, 50); }}
                                data-testid="preset-balanced"
                            >
                                <Scales size={14} className="mr-1" /> Balanced mix
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => { setSelectedDifficulty("hard"); setTimeout(startSession, 50); }}
                                data-testid="preset-focus"
                            >
                                <Flame size={14} className="mr-1" /> Focus mode (all hard)
                            </Button>
                        </div>
                    </div>
                    <div>
                        <label className="overline text-muted-foreground">Questions per session</label>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <div className="inline-flex overflow-hidden rounded-md border border-border" data-testid="count-picker">
                                {[10, 20, 40].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setQuestionCount(n)}
                                        className={`px-5 py-2 text-sm font-medium transition-colors ${questionCount === n ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"}`}
                                        data-testid={`count-${n}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                            <span className="text-xs text-muted-foreground">or custom:</span>
                            <input
                                type="number"
                                min={1}
                                max={200}
                                value={[10, 20, 40].includes(questionCount) ? "" : questionCount}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (!isNaN(v) && v > 0 && v <= 200) setQuestionCount(v);
                                }}
                                placeholder="50"
                                className="h-10 w-24 rounded-md border border-border bg-background px-3 text-sm focus:border-primary focus:outline-none"
                                data-testid="count-custom"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="overline text-muted-foreground">Domain</label>
                        <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                            <SelectTrigger data-testid="domain-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All domains</SelectItem>
                                {domains.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="overline text-muted-foreground">Difficulty</label>
                        <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                            <SelectTrigger data-testid="difficulty-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__any__">Any difficulty</SelectItem>
                                <SelectItem value="balanced">Balanced mix (30/50/20)</SelectItem>
                                <SelectItem value="easy">Easy only</SelectItem>
                                <SelectItem value="medium">Medium only</SelectItem>
                                <SelectItem value="hard">Hard only</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={startSession} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="start-session-btn">
                        Begin {questionCount}-question session <ArrowRight size={16} className="ml-2" />
                    </Button>
                </div>
            </div>
        );
    }

    if (!q) return null;

    return (
        <div className="mx-auto max-w-3xl px-6 py-12" data-testid="session-screen">
            {preloadedLabel && (
                <div className="mb-4 border border-primary/40 bg-primary/10 p-3 text-sm" data-testid="retake-banner">
                    <span className="overline text-primary">Retake session</span> · {preloadedLabel}
                </div>
            )}
            <div className="mb-6 flex items-center justify-between">
                <div className="overline text-muted-foreground" data-testid="session-progress">Question {idx + 1} of {questions.length}</div>
                <div className="flex items-center gap-4">
                    {isExam && <div className="font-mono text-lg" data-testid="exam-timer">{timeStr}</div>}
                    <Button variant="ghost" size="sm" onClick={toggleBookmark} data-testid="bookmark-btn">
                        <BookmarkSimple weight={bookmarks.has(q.id) ? "fill" : "regular"} size={16} className={bookmarks.has(q.id) ? "text-primary" : ""} />
                    </Button>
                </div>
            </div>

            <div className="border border-border bg-card p-8">
                <div className="overline mb-3 text-primary">{q.domain}</div>
                <p className="text-xl leading-relaxed">{q.question}</p>
                <div className="mt-6 space-y-3">
                    {q.options.map((opt, i) => {
                        const isSelected = selected === i;
                        let cls = "border-border bg-background hover:border-primary/50";
                        if (reveal) {
                            if (i === q.correct_index) cls = "border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/20";
                            else if (isSelected) cls = "border-destructive bg-destructive/5";
                        } else if (isSelected) {
                            cls = "border-primary bg-primary/5";
                        }
                        return (
                            <button
                                key={i}
                                onClick={() => pick(i)}
                                className={`w-full border p-4 text-left transition-colors ${cls}`}
                                data-testid={`option-${i}`}
                            >
                                <div className="flex items-start gap-3">
                                    <span className="font-mono text-sm text-primary">{String.fromCharCode(65 + i)}</span>
                                    <span className="text-sm">{opt}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {reveal && !isExam && (
                    <div className="mt-6 border-l-2 border-primary bg-secondary/40 p-4" data-testid="explanation">
                        <div className="overline mb-2 text-primary">Explanation</div>
                        <p className="text-sm leading-relaxed">{q.explanation}</p>
                    </div>
                )}
            </div>

            <div className="mt-6 flex justify-between">
                <Button variant="ghost" onClick={prev} disabled={idx === 0} data-testid="prev-btn">Previous</Button>
                {idx < questions.length - 1 ? (
                    <Button onClick={next} disabled={!isExam && !reveal} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="next-btn">
                        Next <ArrowRight size={16} className="ml-2" />
                    </Button>
                ) : (
                    <Button onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="submit-btn">Submit session</Button>
                )}
            </div>
        </div>
    );
}
